import React from 'react';
import { Box } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys, ReferralWithScope_UserWithoutId } from '../../helpers/DataStructures';
import ExclusiveAsyncActionGroup from '../../components/ExclusiveAsyncActionGroup';

interface ReferralInStatusActionsProps {
  referral: ReferralWithScope_UserWithoutId;
  onUpdated?: (updatedReferral: ReferralWithScope_UserWithoutId) => void;
  onUpdateStart?: () => void;
  onUpdateEnd?: () => void;
  statusMessage?: string;
  disabled?: boolean;
}

const ReferralInStatusActions: React.FC<ReferralInStatusActionsProps> = ({
  referral,
  onUpdated,
  onUpdateStart,
  onUpdateEnd,
  statusMessage,
  disabled = false,
}) => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const queryClient = useQueryClient();

  const updateCachedReferralList = (cacheKey: CacheKeys, updatedReferral: ReferralWithScope_UserWithoutId) => {
    queryClient.setQueryData<ReferralWithScope_UserWithoutId[] | undefined>(
      [cacheKey],
      (existingReferrals) => {
        if (!Array.isArray(existingReferrals)) {
          return existingReferrals;
        }

        return existingReferrals.map((existingReferral) =>
          existingReferral.id === updatedReferral.id ? updatedReferral : existingReferral
        );
      }
    );
  };

  const updateStatusMutation = useMutation(
    ({
      referralId,
      newStatus,
      message,
    }: {
      referralId: number;
      newStatus: string;
      message?: string;
    }) =>
      message
        ? apiServiceInstance.setUserReferralsInStatus(referralId, newStatus, message)
        : apiServiceInstance.setUserReferralsInStatus(referralId, newStatus),
    {
      onSuccess: (updatedReferral, variables) => {
        const normalizedUpdatedReferral =
          updatedReferral.status === variables.newStatus
            ? updatedReferral
            : { ...updatedReferral, status: variables.newStatus };

        // REQ-002: write the server-confirmed status straight into every active referral cache.
        queryClient.setQueryData([CacheKeys.REFERRAL_BY_ID, referral.id], normalizedUpdatedReferral);
        updateCachedReferralList(CacheKeys.REFERRALS_TO_USER, normalizedUpdatedReferral);
        updateCachedReferralList(CacheKeys.REFERRALS_FROM_USER, normalizedUpdatedReferral);

        onUpdated?.(normalizedUpdatedReferral);
      },
    }
  );

  const handleUpdateStatus = async (newStatus: string) => {
    const trimmedMessage = statusMessage?.trim();

    onUpdateStart?.();

    try {
      await updateStatusMutation.mutateAsync({
        referralId: referral.id,
        newStatus,
        message: trimmedMessage ? trimmedMessage : undefined,
      });
    } finally {
      onUpdateEnd?.();
    }
  };

  if (referral.status !== 'created' && referral.status !== 'goingAhead') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        marginTop: 2,
      }}
    >
      {referral.status === 'created' && (
        <ExclusiveAsyncActionGroup
          actions={[
            {
              actionId: 'goingAhead',
              // REQ-007: the conversation UI already updates in place, so only failures raise a toast.
              // REQ-011: conversation status actions use the shared quiet variant to stay lower-emphasis.
              errorToast: 'Error',
              idleLabel: "Success - I'm going ahead with this job",
              onAction: () => handleUpdateStatus('goingAhead'),
              pendingLabel: 'Updating...',
              disabled,
              styleVariant: 'green',
              successLabel: 'Referral Updated',
              variant: 'quiet',
            },
            {
              actionId: 'failed-created',
              // REQ-007: the conversation UI already updates in place, so only failures raise a toast.
              errorToast: 'Error',
              idleLabel: "I didn't win this job",
              onAction: () => handleUpdateStatus('failed'),
              pendingLabel: 'Updating...',
              disabled,
              styleVariant: 'red',
              successLabel: 'Referral Updated',
              variant: 'quiet',
            },
            {
              actionId: 'already-customer',
              // REQ-007: the conversation UI already updates in place, so only failures raise a toast.
              errorToast: 'Error',
              idleLabel: 'This is already my customer',
              onAction: () => handleUpdateStatus('alreadyaCustomer'),
              pendingLabel: 'Updating...',
              disabled,
              styleVariant: 'red',
              successLabel: 'Referral Updated',
              variant: 'quiet',
            },
          ]}
        />
      )}
      {referral.status === 'goingAhead' && (
        <ExclusiveAsyncActionGroup
          actions={[
            {
              actionId: 'succeeded',
              // REQ-007: the conversation UI already updates in place, so only failures raise a toast.
              errorToast: 'Error',
              idleLabel: 'Success - Job is complete',
              onAction: () => handleUpdateStatus('succeeded'),
              pendingLabel: 'Updating...',
              disabled,
              styleVariant: 'green',
              successLabel: 'Referral Updated',
              variant: 'quiet',
            },
            {
              actionId: 'failed-going-ahead',
              // REQ-007: the conversation UI already updates in place, so only failures raise a toast.
              errorToast: 'Error',
              idleLabel: "Job didn't go ahead",
              onAction: () => handleUpdateStatus('failed'),
              pendingLabel: 'Updating...',
              disabled,
              styleVariant: 'red',
              successLabel: 'Referral Updated',
              variant: 'quiet',
            },
          ]}
        />
      )}
    </Box>
  );
};

export default ReferralInStatusActions;
